import { useApolloClient, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import GET_COSES from "../../../graphql/queries/ola/getCoses";
import CustomTable from "../../../components/Table";
import Filters from "../../../components/Filters";
import TableSkeleton from "../../../components/Table/components/TableSkeleton";
import useUser from "../../../hooks/useUser";
import CustomButton from "../../../components/CustomButton";
import ExportCosPdf from "../Listing/ExportCosPdf";

import "./index.scss";

const LOCAL_STORAGE_KEYS = {
  page: "pageCertificatesOutgoing",
  sortBy: "sortByCertificatesOutgoing",
  filters: "selectedFiltersCertificatesOutgoing",
};

const buildDefaultFilters = () => ({
  studentFirstName: "",
  studentLastName: "",
  academicYear: "2025/2026",
});

const parseStoredFilters = () => {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.filters);
  if (!stored) {
    return buildDefaultFilters();
  }

  try {
    const parsed = JSON.parse(stored);
    return {
      ...buildDefaultFilters(),
      ...parsed,
    };
  } catch (error) {
    return buildDefaultFilters();
  }
};

const CertificatesOutgoingView = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const apolloClient = useApolloClient();
  const [expandFilters, setExpandFilters] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedFiltersList, setSelectedFiltersList] = useState(
    parseStoredFilters()
  );
  const [sortBy, setSortBy] = useState(
    localStorage.getItem(LOCAL_STORAGE_KEYS.sortBy) !== null
      ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.sortBy))
      : "changed_descending"
  );
  const [page, setPage] = useState(
    localStorage.getItem(LOCAL_STORAGE_KEYS.page) !== null
      ? parseInt(JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.page)))
      : 0
  );
  const pageLimit = 30;
  const exportPageLimit = 500;

  const effectiveFilters = useMemo(
    () => ({
      ...buildDefaultFilters(),
      ...selectedFiltersList,
    }),
    [selectedFiltersList]
  );

  const optionalCosesFilters = useMemo(() => {
    const filters = {
      field_sending_hei_id: user?.heiID,
      field_receiving_academic_year_id: effectiveFilters.academicYear,
    };

    if (effectiveFilters.studentFirstName) {
      filters.field_student_given_names = effectiveFilters.studentFirstName;
    }

    if (effectiveFilters.studentLastName) {
      filters.field_student_family_name = effectiveFilters.studentLastName;
    }

    return filters;
  }, [effectiveFilters]);

  const {
    loading: cosesLoading,
    error: cosesError,
    data: cosesData,
  } = useQuery(GET_COSES, {
    skip: !user?.heiID,
    variables: {
      page: page,
      limit: pageLimit,
      sortBy: sortBy,
      optionalCosesFilters: optionalCosesFilters,
    },
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.page, JSON.stringify(page));
  }, [page]);

  useEffect(() => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.filters,
      JSON.stringify(selectedFiltersList)
    );
  }, [selectedFiltersList]);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.sortBy, JSON.stringify(sortBy));
  }, [sortBy]);

  const formatDate = (value) => {
    if (!value) {
      return "";
    }

    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(value).toLocaleDateString("en-UK", options);
  };

  const tableItems = useMemo(
    () => cosesData?.coses?.data || [],
    [cosesData]
  );

  const tableRows = useMemo(
    () =>
      tableItems.map((item) => ({
        id: item.id,
        first_name: item.field_student_given_names || "",
        last_name: item.field_student_family_name || "",
        arrival_date: formatDate(item.field_arrival_date),
        departure_date: formatDate(item.field_departure_date),
      })),
    [tableItems]
  );

  const exportRowsToCsv = (rows) => {
    const headers = [
      { key: "first_name", label: t("table.firstName", "First Name") },
      { key: "last_name", label: t("table.lastName", "Last Name") },
      { key: "arrival_date", label: t("table.arrivalDate", "Arrival Date") },
      {
        key: "departure_date",
        label: t("table.departureDate", "Departure Date"),
      },
    ];

    const escapeCsvValue = (value) => {
      if (value === null || value === undefined) {
        return "";
      }
      const stringValue = String(value);
      if (/["]|,|\n/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const headerLine = headers.map((h) => escapeCsvValue(h.label)).join(",");
    const dataLines = rows.map((row) =>
      headers
        .map((h) => escapeCsvValue(row[h.key]))
        .join(",")
    );

    const csvContent = [headerLine, ...dataLines].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Certificates Outgoing.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = async () => {
    if (!user?.heiID || isExporting) {
      return;
    }

    setIsExporting(true);
    try {
      const allItems = [];
      let currentPage = 0;
      let total = null;

      while (true) {
        const response = await apolloClient.query({
          query: GET_COSES,
          variables: {
            page: currentPage,
            limit: exportPageLimit,
            sortBy: sortBy,
            optionalCosesFilters: optionalCosesFilters,
          },
          fetchPolicy: "network-only",
        });

        const data = response?.data?.coses?.data || [];
        const count = response?.data?.coses?.count || 0;

        if (total === null) {
          total = count;
        }

        allItems.push(...data);

        if (data.length < exportPageLimit) {
          break;
        }

        if (total !== null && allItems.length >= total) {
          break;
        }

        currentPage += 1;
      }

      const exportRows = allItems.map((item) => ({
        first_name: item.field_student_given_names || "",
        last_name: item.field_student_family_name || "",
        arrival_date: formatDate(item.field_arrival_date),
        departure_date: formatDate(item.field_departure_date),
      }));

      exportRowsToCsv(exportRows);
    } finally {
      setIsExporting(false);
    }
  };

  const totalCount = cosesData?.coses?.count || 0;

  let content = null;

  if (cosesLoading) {
    content = (
      <TableSkeleton
        columnsPerFieldMap={{
          first_name: "col-3",
          last_name: "col-3",
          arrival_date: "col-3",
          departure_date: "col-3",
        }}
      />
    );
  } else if (cosesError) {
    content = (
      <div className="text-center text-danger mx-auto">
        {t("errorMessage")}
      </div>
    );
  } else {
    content = (
      <main className="h-auto text-center mt-4">
        <div className="col-12 pt-0 pl-3 pr-0 mx-auto">
          <div className="mx-1 mx-xxl-5 certificates-outgoing-actions-top">
            <CustomButton
              buttonType="orange"
              text={isExporting ? "Exporting..." : "Export CSV"}
              handleClick={handleExportCsv}
              disabled={isExporting}
            />
          </div>
        </div>
        <CustomTable
          data={tableRows}
          page={page}
          setPage={setPage}
          type="certificates-outgoing"
          expandFilters={expandFilters}
          setExpandFilters={setExpandFilters}
          filters={selectedFiltersList}
          setFilters={(filter) => {
            setSelectedFiltersList((prev) => ({
              ...prev,
              [filter]: "",
            }));
            setPage(0);
          }}
          sortBy={sortBy}
          setSortBy={() => setSortBy("")}
          columnsPerFieldMap={{
            first_name: "col-3",
            last_name: "col-3",
            arrival_date: "col-3",
            departure_date: "col-3",
          }}
          rowClassName="reusable-components-table-row-certificates"
          headerClassName="reusable-components-table-header-certificates"
          pageLimit={pageLimit}
          totalCount={totalCount}
          hasPagination={true}
          hasFilters={true}
        >
          {tableItems.map((item) => (
            <div
              key={item.id}
              className="py-3 px-2 justify-content-end justify-content-lg-around align-items-center bg-none bg-md-light button-triplet-requests flex-grow-1 d-flex gap-3 gap-lg-1"
            >
              <ExportCosPdf
                omobilityId={
                  item.field_omobility_id
                    ? String(item.field_omobility_id)
                    : null
                }
              />
            </div>
          ))}
        </CustomTable>
        <Filters
          expand={expandFilters}
          handleExpand={setExpandFilters}
          selectedFiltersList={selectedFiltersList}
          setSelectedFiltersList={(filters) => {
            setSelectedFiltersList(filters);
            setPage(0);
          }}
          sortBy={sortBy}
          setSortBy={(value) => setSortBy(value)}
          type="certificates-outgoing"
          hideStatus={true}
          hideCreatedBy={true}
        />
      </main>
    );
  }

  return <>{content}</>;
};

export default CertificatesOutgoingView;
